import React, { useState } from 'react';
import {
    Box, Card, CardContent, Typography, Grid, TextField, Button, FormControl,
    InputLabel, Select, MenuItem, Chip, Stack, Paper, Avatar,
    FormControlLabel, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions,
    useTheme, alpha
} from '@mui/material';
import { Groups as GroupsIcon } from '@mui/icons-material';

export default function D1TeamFormation({ step, onStepUpdate, stepMapping, stepId, isReadOnly, batchTimeFormat = '12hour' }) {
    const theme = useTheme();
    const [teamDialogOpen, setTeamDialogOpen] = useState(false);
    const [newMember, setNewMember] = useState({});

    const updateStep = (updates) => {
        onStepUpdate(stepMapping[stepId], { ...step, ...updates });
    };

    // Validation function to check if all required fields are filled
    const isFormValid = () => {
        // Check team lead information
        const hasTeamLead = step.lead?.trim() && 
                           step.leadDepartment?.trim() && 
                           step.leadContact?.trim();

        // Check that we have at least some team members
        const hasTeamMembers = (step.members || []).length >= 3; // Minimum 3 members for cross-functional team

        // Check that team members have required information
        const membersHaveRequiredInfo = (step.members || []).every(member => 
            member.name?.trim() && 
            member.department?.trim() && 
            member.role?.trim() && 
            member.primaryResponsibility && 
            member.availability
        );

        // Check charter and authority
        const hasCharter = step.scope?.trim() && 
                          step.authorityLevel && 
                          step.successCriteria?.trim();

        // Check basic resource allocation
        const hasResourceAllocation = (step.budget !== undefined && step.budget !== null && step.budget !== '') &&
                                     (step.timeAllocation !== undefined && step.timeAllocation !== null && step.timeAllocation !== '');

        // Check that ALL 6 coverage areas are marked (required)
        const hasCoverage = step.coverage_process_knowledge &&
                           step.coverage_quality_compliance &&
                           step.coverage_technical_engineering &&
                           step.coverage_management_authority &&
                           step.coverage_operations &&
                           step.coverage_subject_matter_expert;

        return hasTeamLead && hasTeamMembers && membersHaveRequiredInfo && 
               hasCharter && hasResourceAllocation && hasCoverage;
    };

    return (
        <>
            <Box sx={{ 
                p: 4, 
                backgroundColor: alpha(theme.palette.warning.main, 0.1), 
                borderRadius: 4, 
                border: `1px solid ${theme.palette.warning.main}`, 
                boxShadow: `0 8px 32px ${alpha(theme.palette.warning.main, 0.12)}`,
                backdropFilter: 'blur(20px)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha(theme.palette.warning.main, 0.1)}, ${alpha(theme.palette.warning.main, 0.05)})`,
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <GroupsIcon sx={{ fontSize: 24, color: theme.palette.warning.main }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ 
                            fontWeight: 500, 
                            color: theme.palette.warning.dark, 
                            mb: 0.5
                        }}>
                            D1: Cross-Functional Investigation Team Formation
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Establish a diverse, cross-functional team with the necessary process knowledge and authority to solve the problem.
                        </Typography>
                    </Box>
                </Box>
                
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ 
                            borderRadius: 4, 
                            border: `1px solid ${theme.palette.grey[400]}`,
                            background: 'white',
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Box sx={{
                                        p: 1.5, 
                                        borderRadius: 2, 
                                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                        backdropFilter: 'blur(10px)',
                                        mr: 2
                                    }}>
                                        <GroupsIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                            Team Lead Selection
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Investigation team leadership
                                        </Typography>
                                    </Box>
                                </Box>
                                
                                <Stack spacing={3} sx={{ flexGrow: 1 }}>
                                    <TextField
                                        fullWidth
                                        label="Investigation Team Lead"
                                        value={step.lead || ''}
                                        onChange={isReadOnly ? undefined : (e) => updateStep({ lead: e.target.value })}
                                        InputProps={{ readOnly: isReadOnly }}
                                        placeholder="Name of person leading this investigation"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                            }
                                        }}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Team Lead Department"
                                        value={step.leadDepartment || ''}
                                        onChange={isReadOnly ? undefined : (e) => updateStep({ leadDepartment: e.target.value })}
                                        InputProps={{ readOnly: isReadOnly }}
                                        placeholder="QA, Production, Engineering, etc."
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                            }
                                        }}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Lead Contact Info"
                                        value={step.leadContact || ''}
                                        onChange={isReadOnly ? undefined : (e) => updateStep({ leadContact: e.target.value })}
                                        InputProps={{ readOnly: isReadOnly }}
                                        placeholder="Email or phone"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                            }
                                        }}
                                    />
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                        <Card sx={{ 
                            borderRadius: 4, 
                            border: `1px solid ${theme.palette.grey[400]}`,
                            background: 'white',
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Box sx={{
                                        p: 1.5, 
                                        borderRadius: 2, 
                                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                        backdropFilter: 'blur(10px)',
                                        mr: 2
                                    }}>
                                        <GroupsIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                            Investigation Team ({(step.members || []).length}/8)
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Cross-functional team members
                                        </Typography>
                                    </Box>
                                </Box>
                                
                                <Stack spacing={2} sx={{ flexGrow: 1 }}>
                                    {(step.members || []).map((member, idx) => (
                                        <Card key={idx} sx={{ 
                                            borderRadius: 3,
                                            border: `1px solid ${theme.palette.grey[400]}`,
                                            backgroundColor: alpha(theme.palette.grey[100], 0.8),
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            backdropFilter: 'blur(10px)',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                                            }
                                        }}>
                                            <CardContent sx={{ p: 2 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                            <Avatar 
                                                                sx={{ 
                                                                    width: 32, 
                                                                    height: 32, 
                                                                    backgroundColor: theme.palette.primary.main,
                                                                    fontSize: '0.8rem'
                                                                }}
                                                            >
                                                                {member.name?.charAt(0)}
                                                            </Avatar>
                                                            <Box>
                                                                <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                                                                    {member.name}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {member.role} • {member.department}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                        
                                                        <Box sx={{ mb: 1 }}>
                                                            <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                                                                Primary Responsibility:
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {member.primaryResponsibility || 'Not specified'}
                                                            </Typography>
                                                        </Box>
                                                        
                                                        <Box sx={{ mb: 1 }}>
                                                            <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                                                                Expertise:
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {member.expertise || 'Not specified'}
                                                            </Typography>
                                                        </Box>
                                                        
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                                            {member.skills && member.skills.map((skill, skillIdx) => (
                                                                <Chip 
                                                                    key={skillIdx} 
                                                                    label={skill} 
                                                                    size="small" 
                                                                    variant="outlined"
                                                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                                                />
                                                            ))}
                                                        </Box>
                                                        
                                                        <Box sx={{ mb: 1 }}>
                                                            <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                                                                Availability:
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {member.availability ? member.availability.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Not specified'}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    
                                                    <Button
                                                        size="small"
                                                        color="error"
                                                        onClick={isReadOnly ? undefined : () => {
                                                            const newMembers = [...(step.members || [])];
                                                            newMembers.splice(idx, 1);
                                                            updateStep({ members: newMembers });
                                                        }}
                                                        sx={{ 
                                                            minWidth: 'auto', 
                                                            p: 0.5,
                                                            cursor: isReadOnly ? 'default' : 'pointer'
                                                        }}
                                                    >
                                                        ✕
                                                    </Button>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    ))}
                                    
                                    <Button
                                        variant="outlined"
                                        onClick={isReadOnly ? undefined : () => setTeamDialogOpen(true)}
                                        startIcon={<GroupsIcon />}
                                        fullWidth
                                        sx={{ 
                                            borderRadius: 2,
                                            px: 3,
                                            py: 1.5,
                                            borderColor: theme.palette.primary.main,
                                            color: theme.palette.primary.main,
                                            fontWeight: 500,
                                            cursor: isReadOnly ? 'default' : 'pointer',
                                            opacity: isReadOnly ? 0.6 : 1,
                                            '&:hover': {
                                                borderColor: theme.palette.primary.main,
                                                backgroundColor: alpha(theme.palette.primary.main, 0.05)
                                            }
                                        }}
                                    >
                                        Add Team Member ({(step.members || []).length}/8)
                                    </Button>
                                    
                                    {(step.members || []).length > 0 && (
                                        <Paper sx={{ 
                                            p: 2, 
                                            backgroundColor: alpha(theme.palette.success.main, 0.1), 
                                            border: `1px solid ${theme.palette.success.main}`,
                                            borderRadius: 3,
                                            backdropFilter: 'blur(10px)'
                                        }}>
                                            <Typography variant="caption" sx={{ fontWeight: 500, display: 'block', mb: 1 }}>
                                                Team Skills Overview:
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {Array.from(new Set((step.members || []).flatMap(m => m.skills || []))).map((skill, idx) => (
                                                    <Chip 
                                                        key={idx} 
                                                        label={skill} 
                                                        size="small" 
                                                        sx={{ 
                                                            backgroundColor: alpha(theme.palette.success.main, 0.2), 
                                                            color: theme.palette.success.dark,
                                                            fontSize: '0.7rem'
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </Paper>
                                    )}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                    
                    <Grid item xs={12}>
                        <Card sx={{ 
                            borderRadius: 4, 
                            border: `1px solid ${theme.palette.grey[400]}`,
                            background: 'white',
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Box sx={{
                                        p: 1.5, 
                                        borderRadius: 2, 
                                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                        backdropFilter: 'blur(10px)',
                                        mr: 2
                                    }}>
                                        <GroupsIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                            Required Team Roles Coverage
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Team role assignments
                                        </Typography>
                                    </Box>
                                </Box>
                                
                                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        Check all coverage areas that your team covers:
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => {
                                                updateStep({
                                                    coverage_process_knowledge: true,
                                                    coverage_quality_compliance: true,
                                                    coverage_technical_engineering: true,
                                                    coverage_management_authority: true,
                                                    coverage_operations: true,
                                                    coverage_subject_matter_expert: true
                                                });
                                            }}
                                            sx={{
                                                borderRadius: 2,
                                                px: 2,
                                                py: 0.5,
                                                borderColor: theme.palette.success.main,
                                                color: theme.palette.success.main,
                                                fontWeight: 500,
                                                '&:hover': {
                                                    borderColor: theme.palette.success.main,
                                                    backgroundColor: alpha(theme.palette.success.main, 0.05)
                                                }
                                            }}
                                        >
                                            ✓ Check All
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => {
                                                updateStep({
                                                    coverage_process_knowledge: false,
                                                    coverage_quality_compliance: false,
                                                    coverage_technical_engineering: false,
                                                    coverage_management_authority: false,
                                                    coverage_operations: false,
                                                    coverage_subject_matter_expert: false
                                                });
                                            }}
                                            sx={{
                                                borderRadius: 2,
                                                px: 2,
                                                py: 0.5,
                                                borderColor: theme.palette.grey[400],
                                                color: theme.palette.grey[600],
                                                fontWeight: 500,
                                                '&:hover': {
                                                    borderColor: theme.palette.grey[600],
                                                    backgroundColor: alpha(theme.palette.grey[600], 0.05)
                                                }
                                            }}
                                        >
                                            ✗ Clear All
                                        </Button>
                                    </Box>
                                </Box>
                                
                                <Grid container spacing={2} sx={{ flexGrow: 1 }}>
                                    {[
                                        { area: 'Process Knowledge', desc: 'Team member familiar with the process' },
                                        { area: 'Quality/Compliance', desc: 'QA expertise and regulatory knowledge' },
                                        { area: 'Technical/Engineering', desc: 'Technical and analytical expertise' },
                                        { area: 'Management Authority', desc: 'Decision-making authority and resources' },
                                        { area: 'Operations', desc: 'Hands-on operational experience' },
                                        { area: 'Subject Matter Expert', desc: 'Specialized technical knowledge' }
                                    ].map((areaObj, idx) => {
                                        const coverageKey = `coverage_${areaObj.area.toLowerCase().replace(/[^a-z]/g, '_')}`;
                                        const isCovered = step[coverageKey] || false;
                                        
                                        return (
                                            <Grid item xs={12} md={6} key={areaObj.area}>
                                                <Paper sx={{ 
                                                    p: 2, 
                                                    backgroundColor: isCovered ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.grey[100], 0.8),
                                                    border: `1px solid ${isCovered ? theme.palette.success.main : theme.palette.grey[400]}`,
                                                    borderRadius: 3,
                                                    backdropFilter: 'blur(10px)',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        transform: 'translateY(-1px)',
                                                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                                                    }
                                                }}
                                                onClick={() => {
                                                    updateStep({ [coverageKey]: !isCovered });
                                                }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={isCovered}
                                                                    onChange={(e) => {
                                                                        e.stopPropagation();
                                                                        updateStep({ [coverageKey]: e.target.checked });
                                                                    }}
                                                                    sx={{ 
                                                                        '& .MuiSvgIcon-root': { 
                                                                            fontSize: 20, 
                                                                            color: isCovered ? theme.palette.success.main : theme.palette.grey[400] 
                                                                        } 
                                                                    }}
                                                                />
                                                            }
                                                            label=""
                                                            sx={{ m: 0 }}
                                                        />
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                                                            {areaObj.area}
                                                        </Typography>
                                                        {isCovered && (
                                                            <Chip 
                                                                label="✓" 
                                                                size="small" 
                                                                sx={{ 
                                                                    backgroundColor: theme.palette.success.main,
                                                                    color: 'white',
                                                                    fontSize: '0.7rem',
                                                                    height: 20,
                                                                    minWidth: 20
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                        {areaObj.desc}
                                                    </Typography>
                                                </Paper>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                                
                                <Box sx={{ mt: 3, p: 2, backgroundColor: alpha(theme.palette.warning.main, 0.1), borderRadius: 3 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                        ALL 6 coverage areas must be checked to complete this step:
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: theme.palette.success.main }} />
                                            <Typography variant="caption">Covered</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: theme.palette.grey[400] }} />
                                            <Typography variant="caption">Required</Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    
                    <Grid item xs={12}>
                        <Card sx={{ 
                            borderRadius: 4, 
                            border: `1px solid ${theme.palette.grey[400]}`,
                            background: 'white',
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Box sx={{
                                        p: 1.5, 
                                        borderRadius: 2, 
                                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                        backdropFilter: 'blur(10px)',
                                        mr: 2
                                    }}>
                                        <GroupsIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                            Team Charter & Authority
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Investigation scope and resources
                                        </Typography>
                                    </Box>
                                </Box>
                                
                                <Grid container spacing={3} sx={{ flexGrow: 1 }}>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={4}
                                            label="Investigation Scope"
                                            value={step.scope || ''}
                                            onChange={isReadOnly ? undefined : (e) => updateStep({ scope: e.target.value })}
                                            InputProps={{ readOnly: isReadOnly }}
                                            placeholder="Define what's included/excluded in investigation. Be specific about boundaries, departments, processes, timeframes, and any limitations."
                                            sx={{ 
                                                mb: 3,
                                                '& .MuiOutlinedInput-root': { 
                                                    '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                    '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                    '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                }
                                            }}
                                        />
                                        <FormControl fullWidth sx={{ mb: 3 }}>
                                            <InputLabel>Authority Level</InputLabel>
                                            <Select
                                                value={step.authorityLevel || ''}
                                                onChange={isReadOnly ? undefined : (e) => updateStep({ authorityLevel: e.target.value })}
                                                inputProps={{ readOnly: isReadOnly }}
                                                label="Authority Level"
                                                sx={{ 
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: theme.palette.grey[400],
                                                        borderWidth: 1
                                                    },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: theme.palette.grey[600],
                                                        borderWidth: 1
                                                    },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: theme.palette.grey[700],
                                                        borderWidth: 1
                                                    }
                                                }}
                                            >
                                                <MenuItem value="investigation_only">Investigation Only</MenuItem>
                                                <MenuItem value="recommendations">Make Recommendations</MenuItem>
                                                <MenuItem value="implement_changes">Implement Changes</MenuItem>
                                                <MenuItem value="full_authority">Full Decision Authority</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={4}
                                            label="Success Criteria"
                                            value={step.successCriteria || ''}
                                            onChange={isReadOnly ? undefined : (e) => updateStep({ successCriteria: e.target.value })}
                                            InputProps={{ readOnly: isReadOnly }}
                                            placeholder="How will success be measured? Define specific, measurable outcomes and KPIs that will indicate investigation completion and effectiveness."
                                            sx={{ 
                                                '& .MuiOutlinedInput-root': { 
                                                    '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                    '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                    '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle1" sx={{ mb: 3, fontWeight: 500 }}>
                                            Resource Allocation Dashboard
                                        </Typography>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
                                                    Investigation Budget
                                                </Typography>
                                                <TextField
                                                    type="number"
                                                    fullWidth
                                                    label="Budget Amount ($)"
                                                    value={step.budget || ''}
                                                    onChange={isReadOnly ? undefined : (e) => updateStep({ budget: e.target.value })}
                                                    placeholder="0"
                                                    InputProps={{
                                                        readOnly: isReadOnly,
                                                        startAdornment: <Typography variant="body2" sx={{ mr: 1 }}>$</Typography>
                                                    }}
                                                    sx={{ 
                                                        '& .MuiOutlinedInput-root': { 
                                                            '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                            '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                            '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                        }
                                                    }}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
                                                    Time Allocation
                                                </Typography>
                                                <TextField
                                                    type="number"
                                                    fullWidth
                                                    label="Hours per Week"
                                                    value={step.timeAllocation || ''}
                                                    onChange={isReadOnly ? undefined : (e) => updateStep({ timeAllocation: e.target.value })}
                                                    placeholder="0"
                                                    InputProps={{
                                                        readOnly: isReadOnly,
                                                        endAdornment: <Typography variant="body2" sx={{ ml: 1 }}>hrs/week</Typography>
                                                    }}
                                                    sx={{ 
                                                        '& .MuiOutlinedInput-root': { 
                                                            '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                            '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                            '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                        }
                                                    }}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
                                                    Equipment Access
                                                </Typography>
                                                <FormControl fullWidth sx={{ mb: 2 }}>
                                                    <InputLabel shrink={true}>Equipment Access</InputLabel>
                                                    <Select
                                                        multiple
                                                        value={step.equipmentAccess || []}
                                                        onChange={isReadOnly ? undefined : (e) => updateStep({ equipmentAccess: e.target.value })}
                                                        inputProps={{ readOnly: isReadOnly }}
                                                        label="Equipment Access"
                                                        renderValue={(selected) => (
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                {selected.map((value) => (
                                                                    <Chip 
                                                                        key={value} 
                                                                        label={value} 
                                                                        size="small" 
                                                                        onDelete={isReadOnly ? undefined : () => {
                                                                            const newEquipment = (step.equipmentAccess || []).filter(eq => eq !== value);
                                                                            updateStep({ equipmentAccess: newEquipment });
                                                                        }}
                                                                        sx={{ 
                                                                            '& .MuiChip-deleteIcon': { 
                                                                                cursor: 'pointer',
                                                                                '&:hover': { 
                                                                                    backgroundColor: alpha(theme.palette.grey[500], 0.1),
                                                                                    borderRadius: '50%'
                                                                                }
                                                                            }
                                                                        }}
                                                                    />
                                                                ))}
                                                            </Box>
                                                        )}
                                                        displayEmpty
                                                        sx={{ 
                                                            borderRadius: 2,
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: theme.palette.grey[400],
                                                                borderWidth: 1
                                                            },
                                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: theme.palette.grey[600],
                                                                borderWidth: 1
                                                            },
                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: theme.palette.grey[700],
                                                                borderWidth: 1
                                                            }
                                                        }}
                                                    >
                                                        <MenuItem value="Production Line">Production Line</MenuItem>
                                                        <MenuItem value="QC Lab">QC Lab</MenuItem>
                                                        <MenuItem value="R&D Lab">R&D Lab</MenuItem>
                                                        <MenuItem value="Microscopy">Microscopy</MenuItem>
                                                        <MenuItem value="Analytical Instruments">Analytical Instruments</MenuItem>
                                                        <MenuItem value="Pilot Plant">Pilot Plant</MenuItem>
                                                        <MenuItem value="Environmental Chamber">Environmental Chamber</MenuItem>
                                                        <MenuItem value="Clean Room">Clean Room</MenuItem>
                                                    </Select>
                                                </FormControl>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <TextField
                                                        fullWidth
                                                        placeholder="Add custom equipment..."
                                                        value={step.customEquipment || ''}
                                                        onChange={isReadOnly ? undefined : (e) => updateStep({ customEquipment: e.target.value })}
                                                        InputProps={{ readOnly: isReadOnly }}
                                                        onKeyPress={(e) => {
                                                            if (e.key === 'Enter' && step.customEquipment?.trim()) {
                                                                const newEquipment = [...(step.equipmentAccess || []), step.customEquipment.trim()];
                                                                updateStep({ 
                                                                    equipmentAccess: newEquipment,
                                                                    customEquipment: ''
                                                                });
                                                            }
                                                        }}
                                                        sx={{ 
                                                            '& .MuiOutlinedInput-root': { 
                                                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                            }
                                                        }}
                                                    />
                                                    <Button
                                                        onClick={isReadOnly ? undefined : () => {
                                                            if (step.customEquipment?.trim()) {
                                                                const newEquipment = [...(step.equipmentAccess || []), step.customEquipment.trim()];
                                                                updateStep({ 
                                                                    equipmentAccess: newEquipment,
                                                                    customEquipment: ''
                                                                });
                                                            }
                                                        }}
                                                        disabled={!step.customEquipment?.trim()}
                                                        sx={{ minWidth: 'auto', px: 2 }}
                                                    >
                                                        Add
                                                    </Button>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                    
                    <Grid item xs={12}>
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 2, 
                            p: 3, 
                            backgroundColor: 'white', 
                            borderRadius: 4, 
                            border: `1px solid ${theme.palette.success.main}`,
                            boxShadow: `0 8px 32px ${alpha(theme.palette.success.main, 0.12)}`,
                            backdropFilter: 'blur(20px)'
                        }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={step.completed || false}
                                        onChange={(e) => {
                                            if (!isFormValid() && e.target.checked) {
                                                alert('Please fill out all required fields before marking as complete:\n\n• Team Lead information (name, department, contact)\n• At least 3 team members with complete information\n• Investigation scope, authority level, and success criteria\n• Budget and time allocation\n• Check ALL 6 coverage areas');
                                                return;
                                            }
                                            updateStep({ completed: e.target.checked });
                                        }}
                                        disabled={!isFormValid()}
                                        sx={{ '& .MuiSvgIcon-root': { fontSize: 28, color: isFormValid() ? theme.palette.success.main : theme.palette.grey[400] } }}
                                    />
                                }
                                label=""
                            />
                            <Typography variant="h6" sx={{ fontWeight: 500, color: isFormValid() ? theme.palette.success.dark : theme.palette.grey[600] }}>
                                D1 Team Formation Complete - Ready for Problem Description
                            </Typography>
                            {!isFormValid() && (
                                <Typography variant="body2" sx={{ color: theme.palette.error.main, fontStyle: 'italic' }}>
                                    (Complete team lead info, add 3+ team members, define charter & resources, check ALL 6 coverage areas)
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* Add Team Member Dialog */}
            <Dialog 
                open={teamDialogOpen} 
                onClose={() => setTeamDialogOpen(false)} 
                maxWidth="md" 
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
                        maxHeight: '90vh'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    fontWeight: 500,
                    fontSize: '1.2rem',
                    color: 'primary.main',
                    borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: alpha(theme.palette.primary.main, 0.05),
                    p: 3
                }}>
                    Add Investigation Team Member
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    <Grid container spacing={3} sx={{ mt: 2 }}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Name"
                                value={newMember?.name || ''}
                                onChange={isReadOnly ? undefined : (e) => setNewMember({...newMember, name: e.target.value})}
                                InputProps={{ readOnly: isReadOnly }}
                                placeholder="First Last"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Department"
                                value={newMember?.department || ''}
                                onChange={isReadOnly ? undefined : (e) => setNewMember({...newMember, department: e.target.value})}
                                InputProps={{ readOnly: isReadOnly }}
                                placeholder="Quality Assurance, Production, Engineering, etc."
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Role/Title"
                                value={newMember?.role || ''}
                                onChange={isReadOnly ? undefined : (e) => setNewMember({...newMember, role: e.target.value})}
                                InputProps={{ readOnly: isReadOnly }}
                                placeholder="Senior Engineer, QA Manager, etc."
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Primary Responsibility"
                                value={newMember?.primaryResponsibility || ''}
                                onChange={isReadOnly ? undefined : (e) => setNewMember({...newMember, primaryResponsibility: e.target.value})}
                                InputProps={{ readOnly: isReadOnly }}
                                placeholder="Investigation Lead, Data Collection, Analysis, etc."
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Availability</InputLabel>
                                <Select
                                    value={newMember?.availability || ''}
                                    onChange={isReadOnly ? undefined : (e) => setNewMember({...newMember, availability: e.target.value})}
                                    inputProps={{ readOnly: isReadOnly }}
                                    label="Availability"
                                    sx={{
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme.palette.grey[400],
                                            borderWidth: 1
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme.palette.grey[600],
                                            borderWidth: 1
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme.palette.grey[700],
                                            borderWidth: 1
                                        }
                                    }}
                                >
                                    <MenuItem value="full_time">Full Time</MenuItem>
                                    <MenuItem value="part_time">Part Time</MenuItem>
                                    <MenuItem value="as_needed">As Needed</MenuItem>
                                    <MenuItem value="limited">Limited</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Expertise"
                                value={newMember?.expertise || ''}
                                onChange={isReadOnly ? undefined : (e) => setNewMember({...newMember, expertise: e.target.value})}
                                InputProps={{ readOnly: isReadOnly }}
                                placeholder="e.g., Chemical Analysis, Process Optimization"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                Skills & Competencies (separate with commas)
                            </Typography>
                            <TextField
                                fullWidth
                                label="Skills"
                                value={newMember?.skillsText || ''}
                                onChange={isReadOnly ? undefined : (e) => {
                                    const skillsText = e.target.value;
                                    const skills = skillsText.split(',').map(s => s.trim()).filter(s => s);
                                    setNewMember({...newMember, skillsText, skills});
                                }}
                                InputProps={{ readOnly: isReadOnly }}
                                placeholder="e.g., HPLC Analysis, GMP, Risk Assessment, Project Management"
                                helperText="Add skills that are relevant to this investigation"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                    }
                                }}
                            />
                            {newMember?.skills && newMember.skills.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                    {newMember.skills.map((skill, idx) => (
                                        <Chip 
                                            key={idx} 
                                            label={skill} 
                                            size="small" 
                                            variant="outlined"
                                            sx={{ fontSize: '0.7rem' }}
                                        />
                                    ))}
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, justifyContent: 'space-between' }}>
                    <Button 
                        onClick={isReadOnly ? undefined : () => {
                            setTeamDialogOpen(false);
                            setNewMember({});
                        }}
                        sx={{
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            fontWeight: 500,
                            px: 3,
                            py: 1,
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                border: `1px solid ${theme.palette.primary.main}`,
                                boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.15)}`
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="contained" 
                        onClick={isReadOnly ? undefined : () => {
                            if (newMember?.name && newMember?.department && newMember?.role && newMember?.primaryResponsibility && newMember?.availability) {
                                const newMembers = [...(step.members || []), newMember];
                                updateStep({ members: newMembers });
                                setTeamDialogOpen(false);
                                setNewMember({});
                            }
                        }}
                        disabled={isReadOnly || !newMember?.name || !newMember?.department || !newMember?.role || !newMember?.primaryResponsibility || !newMember?.availability}
                        sx={{
                            borderRadius: 2,
                            fontWeight: 500,
                            px: 4,
                            py: 1,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                            color: 'white',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                            '&:hover': {
                                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                transform: 'translateY(-1px)',
                                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                            },
                            '&:disabled': {
                                background: alpha(theme.palette.grey[300], 0.5),
                                color: theme.palette.text.disabled
                            },
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                    >
                        Add Team Member
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
} 